//#region Imports and Global Definitions
import { useState, useEffect, } from 'react';
import { StyleSheet, SafeAreaView, Platform, useWindowDimensions, ScrollView, Image, View, FlatList, Pressable } from 'react-native';
import { useTheme } from '@react-navigation/native';
import CustomLabel from '../../../../components/CustomLabel/CustomLabel';
import CustomInput from '../../../../components/CustomInput';
import CustomButton from '../../../../components/CustomButton';
import { useCompany } from '../../../../hooks/useCompany';
import { showMessage } from "react-native-flash-message";
var _ = require('lodash');
import { useTranslation } from 'react-i18next';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useHRAdminEmployees } from '../../../../hooks/useHRAdminEmployees';
import { ActivityIndicator } from 'react-native-paper';
import { DEFAULT_COMPANY_PHOTO } from '../../../../img/DefaultCompanyPhoto';
//#endregion

const HRAdminAuthCompanies = (props) => {

    //#region Const Definitions
    const { width, height } = useWindowDimensions();
    const isPlatformWeb = (Platform.OS === 'web' && width > 992 ? false : true);
    const { colors } = useTheme();
    const styles = makeStyles(colors, width, height, isPlatformWeb);
    const { getCompaniesFullData } = useCompany();
    const { insertCompAuth } = useHRAdminEmployees();
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const { t } = useTranslation();
    const { control, handleSubmit, formState: { errors }, setValue } = useForm({
        defaultValues: {
            selectedCompanies: []
        },
    });
    const selectedCompanies = useWatch({ control, name: 'selectedCompanies' })
    const [companies, setCompanies] = useState([]);
    const [companiesOriginal, setCompaniesOriginal] = useState([]);
    //#endregion

    //#region useEffect
    useEffect(() => {
        fetchCompanies();
    }, [])

    useEffect(() => {
        if (searchQuery !== undefined) {
            findCompaniesFromSearchQuery(searchQuery);
        }
    }, [searchQuery]);
    //#endregion

    //#region Helpers
    const findCompaniesFromSearchQuery = (searchQuery = '') => {
        let companies = companiesOriginal.filter(item => item.name?.toLowerCase().includes(searchQuery?.toLowerCase()));
        setCompanies(companies);
    }

    const onPressButton = (item) => {
        // Check if the company is included in the array
        const isIncluded = selectedCompanies.some(obj => obj.company === item.company);
        if (isIncluded) {
            // Remove the item from the array if it's included
            const updatedCompanies = selectedCompanies.filter(obj => obj.company !== item.company);
            setValue('selectedCompanies', updatedCompanies);
        } else {
            // Add the item to the array if it's not included
            setValue('selectedCompanies', [...selectedCompanies, { company: item.company, employeeid: props?.employee?.employeeID }]);
        }
    };

    const fetchCompanies = () => {
        setIsLoading(true);
        getCompaniesFullData().then((res) => {
            const companies = res?.data;
            setCompanies(companies);
            setCompaniesOriginal(companies);
            setIsLoading(false);
        }).catch(() => {
            console.error('Companies were not fetched!')
            setCompanies([]);
            setCompaniesOriginal([]);
            setIsLoading(false);
        })
    }
    //#endregion

    //#region Listeners
    const onSubmit = data => {
        insertCompAuth(data).then((res) => {
            const status = res?.status;
            if (status === 200) {
                props.changeStep();
            }
            else {
                showMessage({
                    message: t('common:issueMessage'),
                    type: "error",
                    duration: 3000,
                })
            }
        })
    };
    //#endregion

    //#region Loading
    if (isLoading)
        return (
            <View style={styles.loader}>
                <ActivityIndicator animating={true} color={colors.blue} />
            </View>
        )
    //#endregion

    return (
        <SafeAreaView style={styles.mainView}>
            <ScrollView>
                <SafeAreaView style={styles.row}>
                    <SafeAreaView style={styles.column}>
                        {/* search */}
                        <CustomInput
                            value={searchQuery || ""}
                            editable={true}
                            setValue={setSearchQuery}
                            type='SELECT_EMPLOYEE'
                            placeholder={`${t('common:search')} for Companies...`}
                        />
                        <SafeAreaView style={[styles.cardView]}>
                            <FlatList
                                contentContainerStyle={[{
                                    paddingHorizontal: 10,
                                }]}
                                style={{ height: height * 0.5 }}
                                data={companies}
                                keyExtractor={(item, index) => `${item?.value}-${index}`}
                                renderItem={({ item }) => (
                                    <SafeAreaView>
                                        <Controller
                                            control={control}
                                            name="selectedCompanies"
                                            render={({ field: { onChange } }) => (
                                                <Pressable
                                                    onPress={() => onPressButton(item)}
                                                    style={() => [selectedCompanies.some(obj => obj.company === item.company) ? styles.selectedItem : null]}
                                                >
                                                    <SafeAreaView style={styles.employeeContainer}>
                                                        <Image
                                                            style={styles.employeePhotograph}
                                                            source={{
                                                                uri: `data:image/png;base64,${item.logo || DEFAULT_COMPANY_PHOTO}`,
                                                            }}
                                                        />
                                                        <CustomLabel value={item.name} type="IMAGE_TEXT" />
                                                    </SafeAreaView>
                                                </Pressable>
                                            )}
                                            rules={{
                                                required: '*',
                                            }}
                                        />
                                    </SafeAreaView>
                                )}
                                scrollEnabled={true}
                                ListEmptyComponent={
                                    <SafeAreaView
                                        style={{ justifyContent: 'space-around', alignItems: 'center' }}>
                                        <CustomLabel
                                            value={t("common:noResultsFound")}
                                            type="ASSM"
                                        />
                                    </SafeAreaView>
                                }
                            />
                        </SafeAreaView>
                    </SafeAreaView>
                    <SafeAreaView style={styles.column}>
                        <SafeAreaView style={[styles.buttons]}>
                            <CustomButton
                                text={t('common:save')}
                                onPress={handleSubmit(onSubmit)}
                                show="COMP_AUTH_SAVE"
                                type="COMP_AUTH_SAVE"
                            />
                        </SafeAreaView>
                    </SafeAreaView>
                </SafeAreaView>
            </ScrollView>
        </SafeAreaView>

    )
};

//#region Styles
const makeStyles = (colors, width) => StyleSheet.create({
    container: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
    },
    image: {
        flex: 1,
        alignItems: 'center'
    },
    mainView: {
        flex: 1,
        height: '100%'
    },
    subMain: {
        margin: 10,
        borderRadius: 35,
        elevation: (Platform.OS === 'web' && width > 992 ? 3 : 0),
        shadowColor: colors.card_line,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.1,
    },
    rowView: {
        flex: 1,
        flexDirection: 'row',
        justifyContent: 'center'
    },
    cardstyleCompany: {
        margin: 10,
        borderRadius: 5,
        backgroundColor: colors.background_card,
    },
    cardStyleBranch: {
        margin: 30,
        borderRadius: 5,
        borderWidth: 2,
        elevation: 5,
        shadowColor: colors.card_line,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.3,
        shadowRadius: 5,
        backgroundColor: colors.background_card,
        borderColor: colors.buttons_submit,
        flex: 1,
    },
    smallInputBox: {
        flex: 1,
        margin: 10,
    },
    bigInputBox: {
        width: '30%',
        flex: 1
    },
    row: {
        flex: 1,
        flexDirection: "row",
        margin: 5,
    },
    buttons: {
        width: '55%',
        flex: 1,
        justifyContent: 'flex-end',
        alignSelf: 'flex-end'
    },
    cardstyle: {
        padding: 10,
        width: "100%",
        borderRadius: 35,
        backgroundColor: colors.background_card,
        elevation: 0,
        shadowColor: 'transparent',
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0,
        shadowRadius: 0,
    },
    error: {
        color: colors.validation_error,
        alignSelf: 'center',
        fontSize: 14,
    },
    verticalLine: {
        backgroundColor: colors.buttons_primary,
        height: '100%',
        width: 1,
    },
    selectedItem: {
        borderWidth: 4,
        borderColor: colors.buttons_primary,
        elevation: 5,
        shadowColor: colors.card_line,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.3,
        shadowRadius: 5,
    },
    employeeContainer: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        marginVertical: 3,
    },
    column: {
        flex: 1
    },
    cardView: {
        flex: 1,
        margin: '2%',
        borderRadius: 5,
        elevation: 5,
        shadowColor: colors.card_line,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.3,
        shadowRadius: 5,
        backgroundColor: colors.background_card,
        height: '80%'
    },
    cardEmployees: {
        flex: 1,
        marginTop: "1%",
        marginLeft: "2%",
        marginRight: "2%",
    },
    employeePhotograph: {
        width: 50,
        height: 50,
        marginLeft: 10,
        borderRadius: 100,
        borderWidth: 1,
        verticalAlign: 'middle',
    },
});


//#endregion

export default HRAdminAuthCompanies;